import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { SnackbarNotice } from "@/shared/notifications/SnackbarNotice";
import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { updatePassword, updateUserAttributes } from "aws-amplify/auth";
import type { AccountProfile, PlayerProfile } from "@chessla/shared";
import { authErrorCopy } from "@/features/auth/auth-copy";
import { importChessComGames } from "@/features/games/games.api";
import { ApiRequestError } from "@/shared/api/client";
import { INITIAL_GAME_IMPORT_LIMIT } from "../constants";
import { updateMyAccountProfile } from "../profile.api";
import { relinkChessAccount } from "../relink-chess-account";

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 0 } } as const;

export const SettingsPanel = ({
  eyebrow,
  title,
  detail,
  children,
}: {
  eyebrow: string;
  title: string;
  detail: string;
  children: ReactNode;
}) => (
  <Box
    component="section"
    sx={{
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "rgba(240,236,226,.025)",
      p: { xs: 2.25, sm: 3 },
      minWidth: 0,
    }}
  >
    <Typography sx={{ color: "secondary.main", fontSize: ".64rem", fontWeight: 900, letterSpacing: ".14em", mb: 0.8 }}>
      {eyebrow}
    </Typography>
    <Typography component="h2" sx={{ color: "primary.main", fontSize: "1.15rem", fontWeight: 800, mb: 0.6 }}>
      {title}
    </Typography>
    <Typography sx={{ color: "text.secondary", fontSize: ".82rem", lineHeight: 1.55, mb: 2.5 }}>{detail}</Typography>
    {children}
  </Box>
);

export const PersonalDetailsPanel = ({
  account,
  email,
  onSaved,
}: {
  account: AccountProfile;
  email: string;
  onSaved: () => Promise<void>;
}) => {
  const [firstName, setFirstName] = useState(account.firstName);
  const [lastName, setLastName] = useState(account.lastName);
  const [birthdate, setBirthdate] = useState(account.birthdate ?? "");
  const [status, setStatus] = useState<{ severity: "success" | "error"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const maximumBirthdate = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 13);
    return date.toISOString().slice(0, 10);
  }, []);

  useEffect(() => {
    setFirstName(account.firstName);
    setLastName(account.lastName);
    setBirthdate(account.birthdate ?? "");
  }, [account]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setStatus(null);
    try {
      await updateUserAttributes({
        userAttributes: {
          given_name: firstName.trim(),
          family_name: lastName.trim(),
          birthdate,
        },
      });
      await updateMyAccountProfile({ firstName, lastName, birthdate });
      await onSaved();
      setStatus({ severity: "success", text: "Personal details saved." });
    } catch (error) {
      setStatus({ severity: "error", text: error instanceof ApiRequestError ? error.message : authErrorCopy(error) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPanel
      eyebrow="PRIVATE ACCOUNT"
      title="Personal details"
      detail="Your name personalizes Chessla. Your date of birth confirms age eligibility and is never shown publicly or sent to Nikola."
    >
      <Box component="form" onSubmit={(event) => void submit(event)}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
          <TextField
            label="First name"
            autoComplete="given-name"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            inputProps={{ maxLength: 80 }}
            required
            fullWidth
            sx={fieldSx}
          />
          <TextField
            label="Last name"
            autoComplete="family-name"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            inputProps={{ maxLength: 80 }}
            required
            fullWidth
            sx={fieldSx}
          />
          <TextField
            label="Date of birth"
            type="date"
            autoComplete="bday"
            value={birthdate}
            onChange={(event) => setBirthdate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: maximumBirthdate }}
            required
            fullWidth
            sx={fieldSx}
          />
          <TextField label="Login email" value={email} disabled fullWidth sx={fieldSx} />
        </Box>
        {status && (
          <SnackbarNotice severity={status.severity} sx={{ borderRadius: 0, mt: 2 }}>
            {status.text}
          </SnackbarNotice>
        )}
        <Button type="submit" variant="outlined" disabled={saving} sx={{ borderRadius: 0, mt: 2.5 }}>
          {saving ? "SAVING…" : "SAVE DETAILS"}
        </Button>
      </Box>
    </SettingsPanel>
  );
};

export const ChessAccountPanel = ({
  profile,
  onChanged,
}: {
  profile: PlayerProfile;
  onChanged: () => Promise<void>;
}) => {
  const [username, setUsername] = useState(profile.username);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ severity: "success" | "warning" | "error"; text: string } | null>(null);
  const changed = username.trim().toLowerCase() !== profile.username.toLowerCase();

  useEffect(() => {
    setUsername(profile.username);
    setConfirmed(false);
  }, [profile]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!changed || !confirmed) return;
    setSaving(true);
    setStatus(null);
    try {
      if (!(await relinkChessAccount(username))) return;
      await onChanged();
      try {
        const result = await importChessComGames(INITIAL_GAME_IMPORT_LIMIT);
        setStatus({
          severity: "success",
          text: `Chess.com source changed. ${result.gamesImported} recent games imported.`,
        });
      } catch (error) {
        setStatus({
          severity: "warning",
          text:
            error instanceof ApiRequestError
              ? `Source changed, but import stopped: ${error.message} Retry from Analyze.`
              : "Source changed, but import stopped. Retry from Analyze.",
        });
      }
      await onChanged();
    } catch (error) {
      setStatus({
        severity: "error",
        text: error instanceof ApiRequestError ? error.message : "Could not change the Chess.com source.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPanel
      eyebrow="PUBLIC CHESS DATA"
      title="Chess.com connection"
      detail="Use any public Chess.com username. It does not need to be yours and another Chessla account may use the same one."
    >
      <Box component="form" onSubmit={(event) => void submit(event)}>
        <TextField
          label="Chess.com username"
          value={username}
          onChange={(event) => {
            setUsername(event.target.value);
            setConfirmed(false);
          }}
          inputProps={{ maxLength: 100 }}
          required
          fullWidth
          sx={fieldSx}
        />
        {changed && (
          <>
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              Changing source permanently clears imported games, analyses, Patterns, and Nikola chess history before
              importing the new account.
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />}
              label="I understand this replaces the current chess dataset"
              sx={{ mt: 1.25 }}
            />
          </>
        )}
        {status && (
          <SnackbarNotice severity={status.severity} sx={{ borderRadius: 0, mt: 2 }}>
            {status.text}
          </SnackbarNotice>
        )}
        <Button
          type="submit"
          variant="outlined"
          color="secondary"
          disabled={!changed || !confirmed || saving}
          sx={{ borderRadius: 0, mt: 2 }}
        >
          {saving ? "RELINKING…" : "CHANGE CHESS SOURCE"}
        </Button>
      </Box>
    </SettingsPanel>
  );
};

export const PasswordPanel = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ severity: "success" | "error"; text: string } | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirmation) {
      setStatus({ severity: "error", text: "New passwords do not match." });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmation("");
      setStatus({ severity: "success", text: "Password changed." });
    } catch (error) {
      setStatus({ severity: "error", text: authErrorCopy(error) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPanel eyebrow="SECURITY" title="Password" detail="Change the password used to sign in to this account.">
      <Box component="form" onSubmit={(event) => void submit(event)} sx={{ display: "grid", gap: 1.5 }}>
        <TextField
          label="Current password"
          autoComplete="current-password"
          type="password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          required
          fullWidth
          sx={fieldSx}
        />
        <TextField
          label="New password"
          autoComplete="new-password"
          type="password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          inputProps={{ minLength: 8 }}
          required
          fullWidth
          sx={fieldSx}
        />
        <TextField
          label="Confirm new password"
          autoComplete="new-password"
          type="password"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          inputProps={{ minLength: 8 }}
          required
          fullWidth
          sx={fieldSx}
        />
        {status && (
          <SnackbarNotice severity={status.severity} sx={{ borderRadius: 0 }}>
            {status.text}
          </SnackbarNotice>
        )}
        <Button type="submit" variant="outlined" disabled={saving} sx={{ borderRadius: 0, justifySelf: "start" }}>
          {saving ? "UPDATING…" : "CHANGE PASSWORD"}
        </Button>
      </Box>
    </SettingsPanel>
  );
};
